import React, { useMemo } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Grid, Typography } from "@material-ui/core";

import {
  changeControls, fetchAsset,
  fetchAssets,
  setActiveGrade,
} from "../../redux/modules/action.creators/app.actions";
import {
  adjustmentSelector,
  featuresSelector,
  modelSelector,
  modelsSelector,
  partnerListSelector,
} from "../../redux/modules/app.module";

import Seating from "../../types/Seating";
import Model from "../../types/Model";
import Adjustment from "../../types/Adjustment";

import { ContactArticle } from "../../components/contact.article.component";
import Footer from "../../components/footer.component";

import styles from "./styles.module.scss";
import { Overview } from "./overview.component";
import { PBannerComponent } from "./banner.component";
import { Settings } from "react-slick";
import Carousel from "../../components/carousel.component";
import Routes from "../../app.router/router.constants";
import { imageCompressConstants } from "../../constants/imageCompressConstants";
import { Grades } from "./details.viewer/grade.catalog";
import FeaturesList from "./features.list";
import Partner from "../../types/Partner";
import { useGeneratePDF } from "../../utils/componentToPdf/useGeneratePDF";

interface IMapProps {
  seating?: Seating;
  partner?: Partner;
}

export const Product: React.FC<IMapProps> = ({ seating, partner }) => {
  //@ts-ignore
  let { title } = useParams();
  const dispatch = useDispatch();
  const adjustment = useSelector(adjustmentSelector);
  const models = useSelector(modelsSelector);
  const model = useSelector(modelSelector);
  const partnersList = useSelector(partnerListSelector);
  const features = useSelector(featuresSelector);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const buttonRef = React.useRef<HTMLDivElement>(null);

  const isPartnerProduct = Boolean(partner);

  const pdfTitle = model?.title || title;

  const partnersChairs = useMemo<Model[]>(() => {
    const result: Model[] = [];

    if (!partnersList) return [];

    partnersList.forEach((item: Partner) => {
      if (item.products) {
        result.push(...item.products);
      }
    });
    return result;
  }, [partnersList]);

  const availableModels = useMemo<Model[]>(() => {
    if (isPartnerProduct) {
      return partnersChairs;
    }

    return models || [];
  }, [isPartnerProduct, partnersChairs, models]);

  const {
    generatePDF,
    imageRefs,
    recommendedImageRefs,
    featuresImageRefs,
    logoRef,
  } = useGeneratePDF(availableModels, model, adjustment, pdfTitle);

  React.useEffect(() => {
    if (seating && seating.entries)
      dispatch(fetchAssets(seating.entries, "models", { class: Model }));
  }, [seating, dispatch]);

  React.useEffect(() => {
    dispatch(setActiveGrade(null));
    dispatch(changeControls({ name: "activeColor", value: null }));
    dispatch(changeControls({ name: "adjustment", value: null }));
  }, [title, dispatch]);

  React.useEffect(() => {
    const norm = (s: string) => s.split(" ").join("_").toLowerCase();
    const normalizedTitle = title.toLowerCase();

    const selectModel =
      (models || []).find(m => norm(m.title) === normalizedTitle) ||
      (partnersChairs || []).find(m => norm(m.title) === normalizedTitle);

    if (selectModel) {
      dispatch(changeControls({ name: "model", value: selectModel }));
    } else if (models && models.length > 0) {
      dispatch(fetchAsset(title));
    } else if (!models || models.length === 0) {
      dispatch(fetchAsset(title));
    }
  }, [models, partnersChairs, title, dispatch]);

  const modelId = model?.id;
  React.useEffect(() => {
    if (!model?.entries?.[0]) return;

    dispatch(
      fetchAssets([model.entries[0]], "adjustments", { class: Adjustment })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, dispatch]);

  const renderSlider = (serial: string) => {
    const _models = availableModels.filter((model) => model.serial === serial);
    const _contents = _models.reduce((acc, el) => {
      return [
        ...acc,
        {
          id: el.id,
          title: el.title,
          label: el.serial,
          category: el.category,
          thumbnail: `https:${el.angle_thumbnail}${imageCompressConstants.productCardThumb}`,
          price: el.price,
          gsa: el.gsa,
          isFabrix: el.isFabrix,
          isDiscounted: el.isDiscounted,
          discountValue: el.discountValue,
        },
      ];
    }, []);

    const settings: Settings = {
      dots: false,
      swipe: false,
      arrows: true,
      infinite: false,
      speed: 500,
      autoplay: true,
      slidesToShow: 3,
      slidesToScroll: 1,
      dotsClass: "slider_dots",
      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 1,
            swipe: true,
          },
        },
        {
          breakpoint: 1023,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
            swipe: true,
            arrows: false,
            dots: true,
          },
        },
        {
          breakpoint: 720,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            swipe: true,
            arrows: false,
            dots: true,
          },
        },
      ],
    };
    return _contents ? (
      <Carousel
        title={"You may also like"}
        contents={_contents}
        settings={settings}
      />
    ) : null;
  };

  const handleClickSeating = () => {
    dispatch(changeControls({ name: "filter", value: "All" }));
  };

  //PDF generation
  if (!model || !adjustment) return null;
  
  const showContenfulAngleImage = model.adjustment[0].fields?.angleMeasurements !== undefined 
  const showContenfulProfileImage = model.adjustment[0]?.fields?.profileMeasurements !== undefined;  
  
  const angleMeasurementsImage = showContenfulAngleImage ?
   `https:${model.adjustment[0]?.fields.angleMeasurements?.fields.file.url}`:
    "https://images.ctfassets.net/o81fv7n1eubg/13AXxqqBK1FwZiXpFPIKtI/0b4cfbf2fc7740d613aba258fce8668f/Rectangle_1.png";
  const profileMeasurementsImage = showContenfulProfileImage ?
     `https:${model.adjustment[0]?.fields.profileMeasurements?.fields.file.url} ` : 
    "https://images.ctfassets.net/o81fv7n1eubg/13AXxqqBK1FwZiXpFPIKtI/0b4cfbf2fc7740d613aba258fce8668f/Rectangle_1.png";

  const renderRecommendedModels = () => {
    if (availableModels.length) {
      const serial = availableModels.find(
        (model: Model) =>
          model.title.split(" ").join("_").toLowerCase() === title.toLowerCase()
      )?.serial;
      const recommendedModels = availableModels.filter(
        (model) => model.serial === serial
      );

      if (recommendedModels.length > 6) {
        recommendedModels.slice(0, 6);
      }

      return recommendedModels.map((model: Model, index) => (
        <img
          ref={(element) =>
            element && recommendedImageRefs.current.push(element)
          }
          className={styles.image}
          src={`${model.thumbnail}?fm=avif&q=10`}
          key={model.id}
          crossOrigin="anonymous"
          alt={`Recommended Model ${index + 1}`}
        />
      ));
    }
  };

  const renderFeatures = () => {
    if (!adjustment?.features) {
      return null;
    }
    
    return adjustment?.features.map((feature, i) => {
      const featureIcon = features.find(
        (el) => el.key?.toLowerCase() === feature.key?.toLowerCase()
      )?.icon;

      if (feature.key && featureIcon) {
        return (
          <img
            ref={(element) =>
              element && featuresImageRefs.current.push(element)
            }
            alt={feature.key}
            className={styles.image}
            src={`/assets/images/features/${featureIcon}`}
            key={feature.key}
          />
        );
      }
      return null;
    });
  };

  const renderProductPdf = (): void => {
    generatePDF();
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>
          {model &&
            model.title
              .split(" ")
              .map((_el) => _el.charAt(0).toUpperCase() + _el.slice(1))
              .join(" ") + " | Eurotech"}
        </title>
        <meta name="description" content={adjustment?.description} />
      </Helmet>
      <Grid container className={styles.wrapper}>
        <div className={styles.container}>
          <Grid item xs={12} className={styles.sequences}>
            <div className={styles.sequence}>
              <Typography variant="caption" className={styles.label}>
                Seating
              </Typography>
              <span>/</span>
            </div>
            <Link
              to={
                partner
                  ? `${Routes.PartnerPage}/${partner.partnerName
                      .split(" ")
                      .join("_")
                      .toLowerCase()}`
                  : `/${seating?.title?.toLowerCase()}`
              }
              onClick={handleClickSeating}
              className={styles.sequence}
            >
              <Typography variant="caption" className={styles.label}>
                {seating && !partner
                  ? seating.title
                  : !seating && partner
                  ? partner.title
                  : null}
              </Typography>
              <span>/</span>
            </Link>
            <div className={styles.sequence}>
              <Typography variant="caption" className={styles.label}>
                {model && model?.title}
              </Typography>
            </div>
          </Grid>
        </div>
      </Grid>
      {adjustment && model && (
        <>
          <div className={styles.info}>
            <PBannerComponent
              seating={seating}
              specifications_new={adjustment.specifications_new}
              model={model}
              screenshots={adjustment.screenshots}
              specifications={adjustment.specifications}
            />
            <section
              className={`${styles.container} ${styles.grades}`}
              style={
                model && (model.buyNow || model.configurable)
                  ? { marginBottom: "100px" }
                  : { marginBottom: "50px" }
              }
            >
              <h1 className={styles.grades__title}>Grade / Pricing</h1>
              {model && model.gradeDescription ? (
                <h5 className={styles.grades__subtitle}>
                  {model.gradeDescription}
                </h5>
              ) : null}
              <Grades adjustment={adjustment} model={model} />
            </section>
          </div>
          {model.video && (
            <div className={styles.videoWrapper}>
              <div
                className={styles.playButton}
                ref={buttonRef}
                onClick={() => {
                  if (videoRef.current && buttonRef.current) {
                    const playPromise = videoRef.current.play();
                    if (playPromise !== undefined) {
                      buttonRef.current.style.display = "none";
                      videoRef.current.controls = true;
                      playPromise.then().catch((e) => console.error(e));
                    }
                  }
                }}
              >
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.3924 34.3749C11.9127 34.374 11.4415 34.2483 11.0252 34.01C10.0877 33.4788 9.50488 32.4475 9.50488 31.328V8.67177C9.50488 7.54911 10.0877 6.52099 11.0252 5.98974C11.4514 5.74477 11.9357 5.61879 12.4273 5.62497C12.9189 5.63116 13.3998 5.76928 13.8197 6.02489L33.183 17.6155C33.5866 17.8686 33.9192 18.2199 34.1498 18.6367C34.3804 19.0535 34.5014 19.522 34.5014 19.9983C34.5014 20.4747 34.3804 20.9432 34.1498 21.3599C33.9192 21.7767 33.5866 22.1281 33.183 22.3811L13.8166 33.9749C13.3868 34.2347 12.8946 34.373 12.3924 34.3749Z"
                    fill="#D13532"
                  />
                </svg>
              </div>
              <div className={styles.video}>
                <video ref={videoRef}>
                  <source src={model.video} />
                </video>
              </div>
            </div>
          )}

          <Overview
            renderProductPdf={renderProductPdf}
            description={adjustment.description}
            screenshots={adjustment.screenshots}
            product_sheet={adjustment.product_sheet}
            assembly_instruction={adjustment.assembly_instruction}
            dimension_sheet={adjustment.dimension_sheet}
            assembly_video={adjustment.assembly_video}
            enablePdfDownloading={adjustment.enablePdfDownloading}
          />
          {adjustment.features_new ? (
            <section className={styles.features_wrapper}>
              <div className={styles.container}>
                <h1 className={styles.features_title}>Features</h1>
                <FeaturesList features_new={adjustment.features_new} />
              </div>
            </section>
          ) : (
            <>
              {adjustment.features &&
              adjustment.features.length &&
              Object.keys(adjustment.features[0]).length ? (
                <section className={styles.features_wrapper}>
                  <div className={styles.container}>
                    <h1 className={styles.features_title}>Features</h1>
                    <FeaturesList features={adjustment.features} />
                  </div>
                </section>
              ) : null}
            </>
          )}
        </>
      )}
      {availableModels.length &&
        renderSlider(
          availableModels.find(
            (model: Model) =>
              model.title.split(" ").join("_").toLowerCase() ===
              title.toLowerCase()
          )?.serial
        )}
      <ContactArticle />
      <Footer />
      <article className={styles.root}>
        <img
          ref={imageRefs[0]}
          className={styles.image}
          src={`${angleMeasurementsImage}?fm=webp`}
          alt="Angle Measurements"
          crossOrigin="anonymous"
        />
        <img
          ref={imageRefs[1]}
          className={styles.image}
          src={`${profileMeasurementsImage}?fm=webp`}
          alt="Profile Measurements"
          crossOrigin="anonymous"
        />
        {renderRecommendedModels()}
        {renderFeatures()}
        <img
          ref={logoRef}
          className={styles.image}
          src={"/assets/images/EUROTECH_Logo_red.png"}
          alt="Logo"
          crossOrigin="anonymous"
        />
      </article>
    </React.Fragment>
  );
};
